import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, TrustBadge, fmtEUR } from "@/components/Common";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  AlertTriangle, ArrowUpRight, ArrowDownRight, Calendar, Sparkles, CheckSquare,
  ChevronDown, ChevronUp, Info, Gauge, Package, ClipboardList, CalendarDays,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

/* -----------------------------------------------------------------------
 * FASE 7.3 — Dashboard as a "control room" (Option A, mobile-first).
 *
 * Sections (in order): OGGI → RICHIEDE ATTENZIONE → DECISIONI → analitiche
 * secondarie (collapsible).
 *
 * KPI Trust: per-METRIC (not per-AREA). The KPI reads its usual endpoint
 * and enriches with a per-metric trust label derived from real metadata
 * (revenue_source_meta.today_source, confidence.cogs, etc.). Data Quality
 * overlay is used only where the specific metric depends on data DQ has
 * flagged INSUFFICIENT.
 * ----------------------------------------------------------------------- */

const today = () => new Date().toISOString().slice(0, 10);

// Daily Close visual state
const DC_STATE = {
  DA_APRIRE:    { tone: "muted",   label: "Da aprire",        color: "text-neutral-500" },
  IN_CORSO:     { tone: "default", label: "In corso",         color: "text-neutral-800" },
  DA_CHIUDERE:  { tone: "default", label: "Da chiudere",      color: "text-amber-800" },
  FIRMATA:      { tone: "good",    label: "Firmata",          color: "text-emerald-800" },
  RIAPERTA:     { tone: "default", label: "Riaperta",         color: "text-amber-800" },
};

function computeDailyCloseState(dc) {
  if (!dc) return { key: "DA_APRIRE", state: DC_STATE.DA_APRIRE, cta: "Apri chiusura giornata" };
  const sig = dc.signature;
  if (sig?.status === "signed")   return { key: "FIRMATA",     state: DC_STATE.FIRMATA,     cta: "Rivedi chiusura", ts: sig.signed_at };
  if (sig?.status === "reopened") return { key: "RIAPERTA",    state: DC_STATE.RIAPERTA,    cta: "Continua chiusura" };
  if (dc.current_state?.can_sign) return { key: "DA_CHIUDERE", state: DC_STATE.DA_CHIUDERE, cta: "Firma chiusura" };
  const vendite = dc.current_state?.steps?.vendite;
  if (vendite && vendite.details?.reading_cumulative != null && vendite.status === "MISSING") {
    return { key: "IN_CORSO", state: DC_STATE.IN_CORSO, cta: "Apri chiusura giornata" };
  }
  return { key: "DA_APRIRE", state: DC_STATE.DA_APRIRE, cta: "Apri chiusura giornata" };
}

// Determine trust level for "Fatturato oggi" based on real source metadata.
function todayRevenueTrust(revMeta) {
  const src = revMeta?.today_source;
  if (!src)                            return { level: "na",          tooltip: "Nessuna evidenza di attività per oggi" };
  if (src === "daily_sales_confirmed") return { level: "confirmed",   tooltip: "Chiusura fiscale confermata" };
  if (src === "cash_register_readings") return { level: revMeta.today_partial ? "provisional" : "medium",
                                                  tooltip: revMeta.today_partial
                                                    ? "Lettura cassa parziale (non ancora chiusura)"
                                                    : "Lettura cassa cumulativa (non ancora chiusura fiscale)" };
  if (src === "detailed_sales")        return { level: "medium",      tooltip: "Vendite dettagliate — nessuna chiusura fiscale" };
  return { level: "na", tooltip: `Fonte: ${src}` };
}

// Section wrapper
function Section({ title, eyebrow, aside, children, testid }) {
  return (
    <section className="mb-6 md:mb-8" data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-3 gap-2 sm:gap-3">
        <div className="min-w-0">
          {eyebrow && <div className="label-eyebrow">{eyebrow}</div>}
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl mt-1 tracking-tight leading-tight">{title}</h2>
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Collapsible({ title, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E2D9C8] rounded-lg bg-white mb-4" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[#FDFBF7]"
        aria-expanded={open}
      >
        <span className="font-serif text-lg">{title}</span>
        {open ? <ChevronUp size={18} strokeWidth={1.5} /> : <ChevronDown size={18} strokeWidth={1.5} />}
      </button>
      {open && <div className="p-5 border-t border-[#E2D9C8]">{children}</div>}
    </div>
  );
}

// ---- Data Quality compact header pill --------------------------------------
function DataQualityPill({ dq }) {
  if (!dq) return null;
  const score = dq.score;
  const conf = dq.score_confidence;
  const label = dq.score_confidence_label;
  const mapConf = { high: "high", medium: "medium", low: "low", insufficient: "insufficient" }[conf] || "na";
  const isNull = score === null || score === undefined;
  return (
    <Link to="/data-quality" className="inline-flex items-center gap-2 border border-[#E2D9C8] bg-white rounded-full pl-2 pr-3 py-1 hover:border-neutral-900" data-testid="dq-header-pill">
      <Gauge size={14} strokeWidth={1.5} className="text-neutral-500" />
      <span className="text-xs">
        <span className="text-neutral-500">Qualità dati</span>{" "}
        {isNull
          ? <span className="font-medium">Non ancora valutabile</span>
          : <>
              <span className="font-medium">{score}/100</span>{" "}
              <span className="text-neutral-500">·</span>{" "}
              <span className="font-medium">Rappr. {label}</span>
            </>
        }
      </span>
      <TrustBadge level={mapConf} testid="dq-header-badge" />
    </Link>
  );
}

// ---- OGGI: KPI Card with per-metric trust ---------------------------------
function TodayTile({ label, value, trust, hint, testid, tone = "default", cta }) {
  return (
    <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white flex flex-col gap-2 min-w-0" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="label-eyebrow leading-tight">{label}</div>
        {trust && <TrustBadge level={trust.level} label={trust.badgeLabel} tooltip={trust.tooltip} />}
      </div>
      <div className={`font-serif text-xl sm:text-2xl lg:text-3xl leading-none tracking-tight break-words ${tone === "danger" ? "text-red-700" : tone === "good" ? "text-emerald-800" : tone === "muted" ? "text-neutral-400" : ""}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-neutral-500 break-words">{hint}</div>}
      {cta && <div className="mt-1">{cta}</div>}
    </div>
  );
}

// ---- Attention item (unified schema) -------------------------------------
function AttentionItem({ item }) {
  const sevDot = {
    critical: "bg-red-800", high: "bg-red-600",
    medium: "bg-amber-600", low: "bg-neutral-400",
  }[item.severity] || "bg-neutral-400";
  const isDQ = item.category === "qualita_dati";
  return (
    <li className="flex items-start gap-3 py-2.5 border-b border-[#E2D9C8] last:border-0" data-testid="attention-item">
      <span className={`w-2 h-2 mt-1.5 rounded-full ${sevDot} shrink-0`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{item.title}</span>
          {isDQ && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200" data-testid="attention-source-dq">
              Qualità dati
            </span>
          )}
        </div>
        {item.detail && <div className="text-xs text-neutral-500 mt-0.5">{item.detail}</div>}
        {item.impact && <div className="text-xs text-neutral-400 mt-0.5 italic">{item.impact}</div>}
      </div>
      {item.cta_href && (
        <Link to={item.cta_href} className="text-xs underline whitespace-nowrap shrink-0 text-neutral-700 hover:text-neutral-900">
          {item.cta_label || "Apri"}
        </Link>
      )}
    </li>
  );
}

// ---- Decisions section with confidence gate -------------------------------
function DecisionsSection({ profit, forecastArea, dq }) {
  const insights = [];
  // Break-even monthly — gated on cm_reliable
  const beMonth = profit?.month?.break_even;
  if (beMonth?.cm_reliable && beMonth.status && beMonth.status !== "insufficient_data") {
    const beRev = beMonth.break_even_revenue;
    const delta = beMonth.delta_to_break_even;
    if (delta != null) {
      insights.push({
        title: delta < 0
          ? `Mancano ${fmtEUR(-delta)} al break-even mensile`
          : `Break-even superato di ${fmtEUR(delta)}`,
        detail: `Break-even: ${beRev == null ? "—" : fmtEUR(beRev)} · giorni rimanenti: ${profit.month.days_remaining}`,
        cta_label: "Vai a Redditività",
        cta_href: "/finance",
        confident: true,
      });
    }
  } else if (profit?.month) {
    insights.push({
      title: "Break-even mensile: dati insufficienti",
      detail: "Copertura food cost o costi fissi incompleti — non affidabile.",
      cta_label: "Vai a Qualità dati",
      cta_href: "/data-quality",
      confident: false,
    });
  }
  // Trend 7g — gated on numeric availability (uses `sales`, often insufficient)
  const t7 = profit?.trend?.revenue_7d_pct;
  if (t7 !== null && t7 !== undefined) {
    insights.push({
      title: `Trend 7g vs 7g precedenti: ${t7 >= 0 ? "+" : ""}${t7}%`,
      detail: t7 < -5 ? "Attenzione, incassi in calo." : t7 > 5 ? "Incassi in crescita." : "Andamento stabile.",
      cta_label: "Vai a Vendite", cta_href: "/sales",
      confident: true,
    });
  } else {
    insights.push({
      title: "Storico vendite dettagliate insufficiente",
      detail: "Il trend a 7 giorni usa le vendite dettagliate. Popola la fonte per abilitare confronti.",
      cta_label: "Vai a Qualità dati", cta_href: "/data-quality",
      confident: false,
    });
  }
  // Food cost — gated on cogs confidence
  const cogsConf = profit?.month?.confidence?.cogs;
  const fc = profit?.month?.food_cost_ratio_pct;
  if (cogsConf === "actual" && fc != null) {
    insights.push({
      title: `Food cost mensile: ${fc}%`,
      detail: fc > 35 ? "Sopra la soglia di riferimento (35%)." : "Nella soglia di riferimento.",
      cta_label: "Vai a Redditività", cta_href: "/finance",
      confident: true,
    });
  } else if (cogsConf === "insufficient_data") {
    insights.push({
      title: "Food cost: dati insufficienti",
      detail: "Ricette o costi ingredienti incompleti — analisi non affidabile.",
      cta_label: "Vai a Qualità dati", cta_href: "/data-quality?area=food_cost",
      confident: false,
    });
  }

  const trimmed = insights.slice(0, 4);
  if (trimmed.length === 0) return null;
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="decisions-list">
      {trimmed.map((d, i) => (
        <li key={i}
            className={`border rounded-lg p-4 bg-white ${d.confident ? "border-[#E2D9C8]" : "border-neutral-300 bg-neutral-50"}`}
            data-testid={`decision-item-${i}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-sm">{d.title}</div>
            {!d.confident && (
              <TrustBadge level="insufficient" label="Dati insuff." tooltip="Confidence gate: dato non affidabile per una decisione" />
            )}
          </div>
          {d.detail && <div className="text-xs text-neutral-500 mb-2">{d.detail}</div>}
          {d.cta_href && (
            <Link to={d.cta_href} className="text-xs underline text-neutral-700 hover:text-neutral-900">
              {d.cta_label} →
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---- AI Manager (kept in secondary section) ------------------------------
function AiManagerCard() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/manager-brief");
      setBrief(data);
    } finally { setLoading(false); }
  };
  return (
    <div data-testid="ai-manager-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.5} />
          <div className="label-eyebrow">AI Manager · Oggi</div>
        </div>
        <Button size="sm" variant={brief ? "outline" : "default"} onClick={load} disabled={loading} data-testid="ai-manager-load">
          {loading ? "Analisi in corso…" : brief ? "Rigenera" : "Genera briefing"}
        </Button>
      </div>
      {!brief && !loading && (
        <p className="text-sm text-neutral-500">L'AI Manager interpreta KPI deterministici. Non calcola i numeri e non sostituisce Qualità dati.</p>
      )}
      {brief?.brief && (
        <pre className="whitespace-pre-wrap text-sm text-neutral-800 leading-relaxed font-sans" data-testid="ai-brief-text">
          {brief.brief}
        </pre>
      )}
    </div>
  );
}

// ---- Documents small card (only "urgent" — <=15gg or scaduti) ------------
const DOC_CAT_LABEL = {
  fatture_attive: "Fatture attive", fatture_passive: "Fatture passive", contratti: "Contratti",
  chiusure_fiscali: "Chiusure fiscali", certificati_haccp: "Certificati HACCP", corsi_formazione: "Corsi & formazione",
  dipendenti: "Documenti dipendenti", assicurazioni: "Assicurazioni", autorizzazioni: "Autorizzazioni",
  ricevute: "Ricevute", manutenzioni: "Manutenzioni", altri: "Altri",
};

/* ========================================================================
 * MAIN
 * ==================================================================== */
export default function Dashboard() {
  // FASE 7.4 STEP F — role-aware composition. All widgets rendered via can().
  const { can } = useAuth();
  const showSales     = can("sales.read");
  const showFinancial = can("dashboard.view_financial");
  const showInventory = can("inventory.read");
  const showOrders    = can("orders.read");
  const showTasks     = can("tasks.read");
  const showShifts    = can("shifts.read");
  const showHaccp     = can("haccp.execute");
  const showDQ        = can("data_quality.view");
  const showDailyClose = can("daily_close.read");
  const showAI        = can("ai_manager.use");
  const showDocs      = can("documents.read.all") || can("documents.read.haccp_only");
  const showForecast  = can("forecast.view");
  const canSign       = can("daily_close.sign");

  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [profit, setProfit] = useState(null);
  const [attention, setAttention] = useState(null);
  const [dq, setDq] = useState(null);
  const [dc, setDc] = useState(null);
  const [staffing, setStaffing] = useState(null);
  const [haccpToday, setHaccpToday] = useState(null);

  useEffect(() => {
    (async () => {
      const iso = today();
      // STEP F — skip requests to endpoints the user cannot access.
      const noop = () => Promise.resolve({ data: null });
      const [d, f, p, a, q, c, st, h] = await Promise.all([
        api.get("/dashboard"),
        showFinancial ? api.get("/cashflow-forecast").catch(() => ({ data: null })) : noop(),
        showFinancial ? api.get("/profitability/summary").catch(() => ({ data: null })) : noop(),
        api.get("/attention/unified?limit=5").catch(() => ({ data: null })),
        showDQ ? api.get("/data-quality/overview").catch(() => ({ data: null })) : noop(),
        showDailyClose ? api.get(`/daily-close/${iso}`).catch(() => ({ data: null })) : noop(),
        showFinancial ? api.get("/staffing/forecast?days=1").catch(() => ({ data: null })) : noop(),
        showHaccp ? api.get("/checklist-runs/status-today").catch(() => ({ data: null })) : noop(),
      ]);
      setData(d.data); setForecast(f.data); setProfit(p.data);
      setAttention(a.data); setDq(q.data); setDc(c.data);
      setStaffing(st.data); setHaccpToday(h.data);
    })();
  }, [showFinancial, showDQ, showDailyClose, showHaccp]);

  const dcState = useMemo(() => computeDailyCloseState(dc), [dc]);
  const todayTrust = useMemo(() => todayRevenueTrust(data?.revenue_source_meta), [data]);
  const forecastArea = useMemo(
    () => (dq?.areas || []).find((a) => a.area === "forecast"),
    [dq]
  );

  if (!data) return <div className="text-neutral-500 p-6" data-testid="dashboard-loading">Caricamento…</div>;

  // OGGI tiles: fatturato · daily close · scontrini · haccp · staff
  const confirmed = dc?.current_state?.steps?.vendite?.details;
  const receipts = confirmed?.receipt_count;
  const receiptTrust = confirmed?.confirmed_revenue != null
    ? { level: "confirmed", tooltip: "Da chiusura confermata di oggi" }
    : { level: "na", tooltip: "Nessuna chiusura oggi" };

  // HACCP today
  const haccpConfig = (haccpToday?.required || 0) > 0;
  const haccpDone = haccpToday?.completed || 0;
  const haccpReq = haccpToday?.required || 0;
  const haccpTrust = !haccpConfig
    ? { level: "na", badgeLabel: "Non configurato", tooltip: "Nessun template HACCP required_daily" }
    : haccpDone >= haccpReq
      ? { level: "high", badgeLabel: "Completo", tooltip: `${haccpDone}/${haccpReq} checklist` }
      : haccpDone > 0
        ? { level: "medium", badgeLabel: "Parziale", tooltip: `${haccpDone}/${haccpReq} checklist` }
        : { level: "insufficient", badgeLabel: "Da compilare", tooltip: `0/${haccpReq} checklist` };

  // Staff today from staffing forecast (day 0)
  const staffToday = staffing?.days?.[0];
  const staffPlanned = staffToday?.slots?.reduce((sum, s) => {
    return sum + Object.values(s.areas || {}).reduce((a, i) => a + (i.planned || 0), 0);
  }, 0);
  const staffConfigured = (staffing?.days || []).length > 0;
  const staffTrust = !staffConfigured
    ? { level: "na", badgeLabel: "Non configurato", tooltip: "Nessun dipendente o turni" }
    : staffPlanned > 0
      ? { level: "high", badgeLabel: "Pianificato", tooltip: `${staffPlanned} risorse pianificate` }
      : { level: "insufficient", badgeLabel: "Nessun turno", tooltip: "Nessun turno pianificato oggi" };

  const liqTone = (data.liquidity ?? 0) < 3000 ? "danger" : "default";

  // STEP F — compute today's tile visibility. We render only widgets whose
  // source permission the user holds. Empty state = authorized but no data;
  // omitted widget = unauthorized (spec §19).
  const todayTiles = [
    showSales && (
      <TodayTile
        key="revenue"
        testid="today-revenue"
        label="Fatturato oggi"
        value={fmtEUR(data.today_revenue)}
        trust={todayTrust}
        hint={data.revenue_source_meta?.today_source
          ? `Fonte: ${data.revenue_source_meta.today_source === "daily_sales_confirmed" ? "Chiusura confermata" : "Lettura cassa"}`
          : (data.today_revenue === 0 ? "Nessuna attività registrata oggi" : null)}
      />
    ),
    showDailyClose && (
      <TodayTile
        key="dc"
        testid="today-daily-close"
        label="Chiusura giornata"
        value={dcState.state.label}
        tone={dcState.state.tone}
        hint={dcState.ts ? `Firmata: ${dcState.ts.slice(0, 16).replace("T", " ")}` : (dc?.current_state?.completion?.label ? `Completamento ${dc.current_state.completion.label}` : null)}
        cta={
          <Link to="/daily-close" className="text-xs underline text-neutral-700 hover:text-neutral-900" data-testid="today-daily-close-cta">
            {dcState.cta} →
          </Link>
        }
      />
    ),
    showSales && (
      <TodayTile
        key="receipts"
        testid="today-receipts"
        label="Scontrini"
        value={receipts != null ? receipts : "—"}
        trust={receiptTrust}
        hint={receipts == null ? "Da chiusura fiscale" : `Scontrino medio ${confirmed?.confirmed_revenue && receipts > 0 ? fmtEUR(confirmed.confirmed_revenue / receipts) : "—"}`}
      />
    ),
    showHaccp && (
      <TodayTile
        key="haccp"
        testid="today-haccp"
        label="HACCP oggi"
        value={haccpConfig ? `${haccpDone}/${haccpReq}` : "—"}
        trust={haccpTrust}
        hint={haccpConfig ? (haccpToday?.anomalies_total ? `${haccpToday.anomalies_total} anomalie` : "Nessuna anomalia") : "Non configurato"}
      />
    ),
    showInventory && (data.under_min_count != null || data.expiring_count != null) && (
      <TodayTile
        key="inventory"
        testid="today-inventory"
        label="Magazzino"
        value={
          <span className="inline-flex items-center gap-2">
            <Package size={20} strokeWidth={1.5} className="text-neutral-500" />
            {(data.under_min_count || 0) + (data.expiring_count || 0)}
          </span>
        }
        tone={((data.under_min_count || 0) + (data.expiring_count || 0)) > 0 ? "danger" : "good"}
        hint={
          ((data.under_min_count || 0) + (data.expiring_count || 0)) === 0
            ? "Nessuna criticità"
            : `${data.under_min_count || 0} sotto scorta · ${data.expiring_count || 0} in scadenza`
        }
        cta={
          <Link to="/inventory" className="text-xs underline text-neutral-700 hover:text-neutral-900" data-testid="today-inventory-cta">
            Vai a magazzino →
          </Link>
        }
      />
    ),
    showOrders && data.orders_incoming != null && (
      <TodayTile
        key="orders"
        testid="today-orders"
        label="Ordini in arrivo"
        value={
          <span className="inline-flex items-center gap-2">
            <ClipboardList size={20} strokeWidth={1.5} className="text-neutral-500" />
            {data.orders_incoming || 0}
          </span>
        }
        hint={
          (data.orders_incoming || 0) === 0
            ? "Nessun ordine in consegna"
            : `${data.orders_incoming} ordini inviati/confermati/in consegna`
        }
        cta={
          <Link to="/orders" className="text-xs underline text-neutral-700 hover:text-neutral-900" data-testid="today-orders-cta">
            Vai a ordini →
          </Link>
        }
      />
    ),
    showTasks && (data.tasks_today_count != null || data.tasks_overdue_count != null) && (
      <TodayTile
        key="tasks"
        testid="today-tasks"
        label="Task oggi"
        value={
          <span className="inline-flex items-center gap-2">
            <CalendarDays size={20} strokeWidth={1.5} className="text-neutral-500" />
            {data.tasks_today_count || 0}
          </span>
        }
        tone={(data.tasks_overdue_count || 0) > 0 ? "danger" : "default"}
        hint={
          (data.tasks_overdue_count || 0) > 0
            ? `${data.tasks_overdue_count} scadute`
            : "Nessuna task scaduta"
        }
        cta={
          <Link to="/tasks" className="text-xs underline text-neutral-700 hover:text-neutral-900" data-testid="today-tasks-cta">
            Vai all'agenda →
          </Link>
        }
      />
    ),
    showShifts && (
      <TodayTile
        key="staff"
        testid="today-staff"
        label="Personale in turno"
        value={staffConfigured ? (staffPlanned || 0) : "—"}
        trust={staffTrust}
        hint={staffConfigured ? "Da fabbisogno di oggi" : "Non configurato"}
      />
    ),
  ].filter(Boolean);

  // FASE 7.4 STEP F.1 — Tailwind static grid map (dynamic class names are
  // NOT extracted by Tailwind's JIT compiler). Explicit literal classnames
  // for 1..8 tiles guarantee the required CSS is emitted at build time.
  const TILES_GRID_MAP = {
    1: "grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 gap-3",
    2: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3",
    3: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3",
    4: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3",
    5: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3",
    6: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3",
    7: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3",
    8: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3",
  };
  const tilesGrid = TILES_GRID_MAP[Math.min(todayTiles.length || 1, 8)] || TILES_GRID_MAP[5];

  return (
    <div data-testid="dashboard-page" className="pb-12">
      <PageHeader
        eyebrow="Cabina di comando"
        title="Panoramica"
        subtitle="Cosa succede oggi · cosa richiede attenzione · cosa decidere."
        actions={
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto md:justify-end">
            {showDQ && <DataQualityPill dq={dq} />}
            {showDailyClose && (
            <Link to="/daily-close" data-testid="dashboard-daily-close-shortcut" className="ml-auto md:ml-0">
              <Button className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7] min-h-[40px]">
                <CheckSquare size={14} strokeWidth={1.5} /> Chiusura giornata
              </Button>
            </Link>
            )}
          </div>
        }
      />

      {/* ========================= OGGI ========================= */}
      {todayTiles.length > 0 && (
      <Section eyebrow="Oggi" title="Stato della giornata" testid="section-oggi">
        <div className={tilesGrid}>
          {todayTiles}
        </div>
      </Section>
      )}

      {/* ================ RICHIEDE ATTENZIONE ================= */}
      <Section
        eyebrow="Priorità"
        title="Richiede attenzione"
        testid="section-attention"
        aside={
          <div className="text-xs text-neutral-500 flex items-center gap-2 sm:gap-3 flex-wrap">
            {attention && (
              <>
                <span data-testid="attention-count">{attention.count}/5</span>
                <span className="text-neutral-300 hidden sm:inline">·</span>
                <span>crit {attention.counts_by_severity.critical}</span>
                <span>alti {attention.counts_by_severity.high}</span>
                <span>medi {attention.counts_by_severity.medium}</span>
              </>
            )}
          </div>
        }
      >
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="attention-panel">
          {attention?.items?.length > 0 ? (
            <ul className="divide-y divide-transparent" data-testid="attention-list">
              {attention.items.map((it, i) => <AttentionItem key={i} item={it} />)}
            </ul>
          ) : (
            <div className="text-sm text-neutral-500 py-4 flex items-center gap-2" data-testid="attention-empty">
              <AlertTriangle size={14} strokeWidth={1.5} />
              Nessuna criticità operativa rilevata.
            </div>
          )}
          {attention?.total_before_cap > attention?.count && (
            <div className="pt-3 mt-3 border-t border-[#E2D9C8] text-xs">
              <Link to="/data-quality" className="underline">Vedi tutte le azioni di Qualità dati →</Link>
            </div>
          )}
        </div>
      </Section>

      {/* ================ DECISIONI ================= */}
      {(showFinancial || showForecast) && (
      <Section eyebrow="Insight" title="Decisioni" testid="section-decisions">
        <DecisionsSection profit={profit} forecastArea={forecastArea} dq={dq} />
      </Section>
      )}

      {/* ================ ANALITICHE SECONDARIE (collapsible) ============ */}
      <Section eyebrow="Approfondimenti" title="Analitiche secondarie" testid="section-secondary">
        {showSales && data.daily_series && (
        <Collapsible title="Andamento incassi — ultimi 14 giorni" defaultOpen testid="collapse-trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.daily_series}>
              <CartesianGrid stroke="#E2D9C8" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C5C5C" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#5C5C5C" }} />
              <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #E2D9C8", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="revenue" stroke="#0A0A0A" strokeWidth={2} dot={false} name="Fatturato" />
              <Line type="monotone" dataKey="cost" stroke="#D7C4A3" strokeWidth={2} dot={false} name="Costi" />
            </LineChart>
          </ResponsiveContainer>
        </Collapsible>
        )}

        {showFinancial && (
        <Collapsible title="Cash flow previsionale" testid="collapse-cashflow">
          {forecast ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-[#E2D9C8] pb-2">
                <span className="text-neutral-500">Oggi (liquidità)</span>
                <span className="font-medium">{fmtEUR(forecast.current)}</span>
              </div>
              {forecast.forecast.map((f) => (
                <div key={f.days} className="flex justify-between">
                  <span className="text-neutral-500">+{f.days} giorni</span>
                  <span className={`font-medium ${f.estimated < 0 ? "text-red-700" : ""}`}>{fmtEUR(f.estimated)}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-[#E2D9C8] text-xs text-neutral-500 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1"><ArrowUpRight size={12} />Media 30gg in: {fmtEUR(forecast.avg_daily_in)}</span>
                <span className="inline-flex items-center gap-1"><ArrowDownRight size={12} />Media 30gg out: {fmtEUR(forecast.avg_daily_out)}</span>
              </div>
              <div className="pt-1 text-[11px] text-neutral-400 flex items-center gap-1">
                <Info size={11} strokeWidth={1.5} /> Fatturato ≠ movimenti finanziari — vista informativa.
              </div>
            </div>
          ) : <div className="text-sm text-neutral-500">—</div>}
        </Collapsible>
        )}

        {showFinancial && data.accounts && (
        <Collapsible title="Liquidità sui conti" testid="collapse-liquidity">
          <StatCard testid="kpi-liquidity" label="Totale liquidità conti"
            value={fmtEUR(data.liquidity)}
            tone={liqTone}
            hint={`${data.accounts.length} conti attivi`}
          />
        </Collapsible>
        )}

        {showAI && (
        <Collapsible title="AI Manager" testid="collapse-ai">
          <AiManagerCard />
        </Collapsible>
        )}

        {showDocs && data.docs_expiring?.length > 0 && (
          <Collapsible title={`Prossime scadenze documenti (${data.docs_expiring.length})`} testid="collapse-docs">
            <div className="divide-y divide-[#E2D9C8]">
              {data.docs_expiring.slice(0, 6).map((d) => {
                const dte = d.days_to_expiry;
                const tone = dte < 0 ? "text-red-800" : dte <= 15 ? "text-red-700" : dte <= 30 ? "text-amber-800" : "text-neutral-700";
                return (
                  <Link key={d.id} to="/documents" data-testid={`dash-doc-exp-${d.id}`}
                    className="flex items-center gap-3 py-2.5 hover:bg-[#FDFBF7]">
                    <Calendar size={14} strokeWidth={1.5} className="text-neutral-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.name}</div>
                      <div className="text-xs text-neutral-500">{DOC_CAT_LABEL[d.category] || d.category} · scad. {d.expiry_date?.slice(0, 10)}</div>
                    </div>
                    <div className={`text-xs font-medium ${tone}`}>{dte < 0 ? `Scaduto da ${-dte}g` : `Fra ${dte}g`}</div>
                  </Link>
                );
              })}
            </div>
          </Collapsible>
        )}
      </Section>
    </div>
  );
}
